import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// Silk smooth cubic bezier (matches existing animations)
const SMOOTH_EASE = 'cubic-bezier(0.65, 0, 0.35, 1)';

export interface ParallaxConfig {
  speed?: number; // Multiplier: 0.5 = slower, 1.5 = faster (default: 1)
  direction?: 'up' | 'down' | 'left' | 'right';
  distance?: number; // Pixels to travel (default: 60)
  scale?: {
    start: number; // Starting scale (default: 1)
    end: number; // Ending scale (default: 1)
  };
  opacity?: {
    start: number; // Starting opacity (default: 1)
    end: number; // Ending opacity (default: 1)
  };
  rotation?: {
    start: number; // Starting rotation in degrees
    end: number; // Ending rotation in degrees
  };
  start?: string; // ScrollTrigger start position (default: "top bottom")
  end?: string; // ScrollTrigger end position (default: "bottom top")
  scrub?: number | boolean; // Scrub value (default: 1.5 for smooth)
  ease?: string; // GSAP easing (default: SMOOTH_EASE)
  markers?: boolean; // Debug markers (default: false)
  disabled?: boolean; // Disable parallax (default: false)
}

let parallaxIdCounter = 0;

export const useParallax = <T extends HTMLElement>(
  ref: React.RefObject<T | null>,
  config: ParallaxConfig = {}
) => {
  const scrollTriggerRef = useRef<ScrollTrigger | null>(null);
  const uniqueIdRef = useRef<string>(`parallax-${++parallaxIdCounter}`);

  useEffect(() => {
    // Only run in browser environment
    if (typeof window === 'undefined') return;

    const element = ref.current;
    if (!element || config.disabled) return;

    // Default configuration
    const {
      speed = 1,
      direction = 'down',
      distance = 60,
      scale,
      opacity,
      rotation,
      start = 'top bottom',
      end = 'bottom top',
      scrub = 1.5,
      ease = SMOOTH_EASE,
      markers = false,
    } = config;

    // Calculate movement based on speed and direction
    const movement = distance * (speed - 1);

    // Build animation properties
    const animationProps: gsap.TweenVars = {};

    // Direction-based movement
    if (direction === 'down' || direction === 'up') {
      const yValue = direction === 'down' ? movement : -movement;
      animationProps.y = yValue;
    } else {
      const xValue = direction === 'right' ? movement : -movement;
      animationProps.x = xValue;
    }

    // Scale animation
    if (scale) {
      gsap.set(element, { scale: scale.start });
      animationProps.scale = scale.end;
    }

    // Opacity animation
    if (opacity) {
      gsap.set(element, { opacity: opacity.start });
      animationProps.opacity = opacity.end;
    }

    // Rotation animation
    if (rotation) {
      gsap.set(element, { rotation: rotation.start });
      animationProps.rotation = rotation.end;
    }

    // Add easing
    animationProps.ease = ease;

    // Create ScrollTrigger animation
    scrollTriggerRef.current = ScrollTrigger.create({
      trigger: element,
      start,
      end,
      scrub,
      markers,
      id: uniqueIdRef.current,
      refreshPriority: -1, // Lower priority so existing animations take precedence
      animation: gsap.to(element, animationProps),
    });

    // Cleanup function
    return () => {
      if (scrollTriggerRef.current) {
        scrollTriggerRef.current.kill();
        scrollTriggerRef.current = null;
      }
    };
  }, [
    ref,
    config.speed,
    config.direction,
    config.distance,
    config.scale?.start,
    config.scale?.end,
    config.opacity?.start,
    config.opacity?.end,
    config.rotation?.start,
    config.rotation?.end,
    config.start,
    config.end,
    config.scrub,
    config.ease,
    config.markers,
    config.disabled,
  ]);

  return scrollTriggerRef;
};
